
import Vue from 'vue';
import { mapGetters, mapActions } from 'vuex';

import SubmitButton from '@/components/controls/submit-button.vue';
import FormInput from '@/components/controls/form-input.vue';
import SelectInput from '@/components/controls/select-input.vue';
import Dropdown, { DropdownItems } from '@/components/controls/dropdown.vue';
import { AdsActions } from '@/store/modules/ads/types';
import { AdStatus } from '@/store/modules/ads/ad';

export default Vue.extend({
  name: 'AdsFilter',
  components: {
    SubmitButton,
    FormInput,
    SelectInput,
    Dropdown,
  },
  props: {
    loading: {
      type: Boolean,
      default: false,
    },
    selectedItems: {
      type: Array,
      default: () => [] as number[],
    },
  },
  computed: {
    ...mapGetters('user/profile', ['isSuperAdmin', 'isAdmin']),
    dropdownItems() {
      const items: DropdownItems = {};
      
      items.reset = {
        label: this.$t('Reset Filters'),
        icon: 'close',
      };
      
      if ((this.isSuperAdmin || this.isAdmin) && this.selectedItems && this.selectedItems.length) {
        items.restore = {
          hasTopBorder: true,
          label: this.$t('Restore'),
          count: this.selectedItems.length,
          disabled: !this.selectedItems.length,
        };

        items.remove = {
          label: this.$t('Delete'),
          count: this.selectedItems.length,
          disabled: !this.selectedItems.length,
        };
      }
      
      return items;
    },
  },
  data() {
    return {
      availableStatuses: {
        [AdStatus.Inactive]: this.$t('Inactive'),
        [AdStatus.Active]: this.$t('Active'),
        scheduled: this.$t('Scheduled'),
        completed: this.$t('Completed'),
      },
      search: '',
      status: '',
      interval: 'all',
      start: '',
      end: '',
      dateError: '',
    };
  },
  methods: {
    ...mapActions('ads', {
      bulkDeleteAds: AdsActions.BULK_DELETE,
      bulkRestoreAds: AdsActions.BULK_RESTORE,
    }),
    initFilters(): void {
      const query = this.$route.query;
      this.interval = typeof query.days === 'string' && ['7', '30', 'custom'].indexOf(query.days) !== -1 ? query.days : 'all';
      this.start = typeof query.start === 'string' ? query.start : '';
      this.end = typeof query.end === 'string' ? query.end : '';
      this.dateError = '';
      this.search = this.$router.currentRoute.query.search || '';
      if (this.$router.currentRoute.query.completed === 'true' || this.$router.currentRoute.query.completed === '1') {
        this.status = 'completed';
      } else if (
        this.$router.currentRoute.query.scheduled === 'true' ||
        this.$router.currentRoute.query.scheduled === '1'
      ) {
        this.status = 'scheduled';
      } else {
        this.status = this.$router.currentRoute.query.status || '';
      }
    },
    applyFilters(): void {
      if (this.loading) return;
      this.dateError = '';
      if (this.interval === 'custom') {
        if (!this.validDate(this.start) || !this.validDate(this.end)) {
          this.dateError = this.$t('Choose a valid start and end date.');
          return;
        }
        if (this.end < this.start) {
          this.dateError = this.$t('End date must be on or after start date.');
          return;
        }
      }
      const { days, start, end, ...queryParams } = this.$route.query;
      const isCompleted = this.status === 'completed';
      const isScheduled = this.status === 'scheduled';

      const location = {
        params: {
          page: '1',
        },
        query: {
          ...queryParams,
          search: this.search,
          status: isCompleted || isScheduled ? '' : this.status,
          completed: isCompleted ? 'true' : '',
          scheduled: isScheduled ? 'true' : '',
          ...(this.interval !== 'all' ? { days: this.interval } : {}),
          ...(this.interval === 'custom' ? { start: this.start, end: this.end } : {}),
        },
      };
      if (this.$router.resolve(location).route.fullPath !== this.$route.fullPath) {
        this.$router.push(location);
      }
    },
    validDate(value: string): boolean {
      if (!/^\d{4}-\d{2}-\d{2}$/.test(value) || value < '0001-01-01') return false;
      const date = new Date(value + 'T00:00:00Z');
      return !isNaN(date.getTime()) && date.toISOString().slice(0, 10) === value;
    },
    handleDropdownClick(action: string): void {
      if ('reset' === action) {
        this.$router.push({
          params: {
            page: 1,
          },
          query: {},
        });
      }

      if ('remove' === action) {
        const answer = window.confirm(this.$t('Do you really want to delete selected ads?'));
        if (answer) {
          this.bulkDeleteAds({ ids: this.selectedItems });
        }
      }

      if ('restore' === action) {
        const answer = window.confirm(this.$t('Do you really want to restore selected ads?'));
        if (answer) {
          this.bulkRestoreAds({ ids: this.selectedItems });
        }
      }
    },
  },
  created(): void {
    this.initFilters();
  },
  watch: {
    $route(): void {
      this.initFilters();
    },
  },
});
